import { Injectable } from '@angular/core';
import { AuthService } from '@klickdata/core/auth';
import { ConfigService } from '@klickdata/core/config';
import { HttpErrorService, RequestBuilderService } from '@klickdata/core/http';
import { AppScope } from '@klickdata/core/resource';
import { Observable } from 'rxjs';
import { catchError, first, map } from 'rxjs/operators';
import { ContentCollection, ContentCollectionData } from './content-collection.model';

@Injectable({
    providedIn: 'root',
})
export class ContentCollectionService {
    protected contentCollectionUrl: string;
    protected customer_id: Observable<number>;

    constructor(
        protected config: ConfigService,
        protected builder: RequestBuilderService,
        protected error: HttpErrorService,
        protected auth: AuthService
    ) {
        this.contentCollectionUrl = `${config.config.apiUrl}collections`;
        // this.contentCollectionUrl = `${config.config.apiUrl}content-collections`;
        this.customer_id = this.auth.getCustomer().pipe(
            first(),
            map((customer) => customer.id)
        );
    }

    public getCustomerContentCollections(scope: AppScope, customerId?: number): Observable<ContentCollection[]> {
        const request = this.builder
            .get<ContentCollectionData[]>(`${this.contentCollectionUrl}`)
            .param('scope', scope)
            .param('translations', 1);
        request.param('customer', customerId || this.customer_id);

        return request
            .request()
            .pipe(map((res) => res.data.map((contentCollection) => this.mapContentCollection(contentCollection))));
    }

    public getPublicContentCollections(): Observable<ContentCollection[]> {
        const request = this.builder.get<ContentCollectionData[]>(`${this.contentCollectionUrl}`);
        request.param('public', 'true');
        return request
            .request()
            .pipe(map((res) => res.data.map((contentCollection) => this.mapContentCollection(contentCollection))));
    }
    public getContentCollectionsByScope(scope: AppScope): Observable<ContentCollection[]> {
        const request = this.builder.get<ContentCollectionData[]>(`${this.contentCollectionUrl}`);
        request.param('scope', scope);
        request.param('translations', 1);
        return request
            .request()
            .pipe(map((res) => res.data.map((contentCollection) => this.mapContentCollection(contentCollection))));
    }

    public updateContentCollection(contentCollectionData: ContentCollectionData): Observable<ContentCollection> {
        const req = this.builder.put<ContentCollectionData>(
            `${this.contentCollectionUrl}/${contentCollectionData.id}`,
            contentCollectionData
        );
        return req.request().pipe(map((res) => this.mapContentCollection(res.data)));
    }

    public setDefaultContentCollection(id: number, customerId: number): Observable<ContentCollection> {
        const req = this.builder.put<ContentCollectionData>(`${this.contentCollectionUrl}/${id}/default`, null);
        if (customerId) {
            req.param('customer', customerId);
        }
        return req.request().pipe(map((res) => this.mapContentCollection(res.data)));
    }

    public addContentCollection(contentCollectionData: ContentCollectionData): Observable<ContentCollection> {
        const req = this.builder.post<ContentCollectionData>(`${this.contentCollectionUrl}`, contentCollectionData);
        return req.request().pipe(map((res) => this.mapContentCollection(res.data)));
    }

    public deleteContentCollection(id: number): any {
        return this.builder
            .delete<any>(`${this.contentCollectionUrl}/${id}`)
            .request()
            .pipe(
                map((res) => res.data),
                catchError((err) => this.error.handle(err))
            );
    }

    protected mapContentCollection(contentCollectionData: ContentCollectionData): ContentCollection {
        const contentCollection = new ContentCollection(contentCollectionData);
        return contentCollection;
    }
}
